import * as React from 'react';
import { ComponentsBookieFallback as BookieFallback } from '@fpsm/cxy-icons/glyphs/components';

import styles from './bookie-logo.module.css';

export enum LOGO_SIZE {
  LARGE = 'large',
  SMALL = 'small',
}

/**
 * The three logo inputs are optional because rendering `BookieFallback` for a bookmaker it cannot
 * resolve is this component's contract, not an accident of the NGF-7059 prop narrowing: the shape it
 * replaced took the whole map and reached the same branch through `bookmakers={{}}`, which is how its
 * own spec covered the fallback. Callers thread `bookie?.x` straight through.
 */
export interface IProps {
  bookmakerName?: string;
  brandColour?: string;
  className?: string;
  /** Resolved by the caller, which already holds the bookmakers map — see NGF-7059. */
  logoUrl?: string;
  noPadding?: boolean;
  onClick?: () => void;
  /** Only selects the fallback SVG's dimensions; the caller picks which logo `logoUrl` is. */
  size: LOGO_SIZE;
  testId?: string;
  withBackground?: boolean;
}

const BookieLogo: React.FC<IProps> = ({
  bookmakerName,
  brandColour,
  className = '',
  logoUrl,
  noPadding = false,
  onClick,
  size,
  testId = 'bookie-logo',
  withBackground = true,
}) => {
  const [error, setError] = React.useState(false);

  if (error || !brandColour || !logoUrl) {
    const width = size === LOGO_SIZE.LARGE ? 190 : 40;
    const height = size === LOGO_SIZE.LARGE ? 50 : 40;

    return (
      <BookieFallback
        className={[styles.bookieLogo, noPadding ? styles.noPadding : '', className].join(' ')}
        height={height}
        width={width}
      />
    );
  }

  return (
    <img
      alt={bookmakerName ? `${bookmakerName} logo` : 'Bookmaker logo'}
      className={[styles.bookieLogo, noPadding ? styles.noPadding : '', className].join(' ')}
      data-testid={testId}
      onClick={onClick}
      onError={() => {
        setError(true);
      }}
      src={logoUrl}
      style={{ background: withBackground ? brandColour : '' }}
    />
  );
};

export default BookieLogo;
